import React, { Fragment, ReactNode, useState, useRef, useEffect, useLayoutEffect, useContext, useMemo } from "react";
import cn from "classnames";
import { Funnel, FunnelNode, FIRST_NODE_LABEL } from "../tools/funnel";
import SvgConnector from "../common/SvgConnector";
import SvgDeadEnd from "../common/SvgDeadEnd";
import ScaleControls from "../common/ScaleControls";
import useDragToScroll from "../hooks/useDragToScroll";
import { SettingsContext } from "../SettingsProvider";
import TrackingTooltip from "../common/TrackingTooltip";
import { LabelStyle } from "../types";
import { formatWeight, formatFloat, getTransparentRGB } from "../tools";

import styles from "./FunnelCanvas.module.scss";

const NODE_WIDTH = 200;
const MIN_NODE_HEIGHT = 45;
const MIN_CANVAS_HEIGHT = 170;
const DEFAULT_CANVAS_HEIGHT = 500;
const MAX_SCALE = 1.5;
const MIN_SCALE = 0.1;
const GOLDEN_RATIO = 0.618;
const GOLDEN_RATIO_REST = 1 - GOLDEN_RATIO;

interface Props {
    funnel: Funnel;
    trackedLabels: Set<string>;
    onSelectNode: (node: FunnelNode | null) => void;
    selectedNode?: FunnelNode;
    showDeadEnds?: boolean;
}

const FunnelCanvas: React.FC<Props> = function ({
    funnel,
    trackedLabels,
    onSelectNode,
    selectedNode,
    showDeadEnds
}: Props) {
    const {
        settings: { numberColumn, unitTag },
        labels
    } = useContext(SettingsContext);
    const labelColors: { [id: string]: LabelStyle } = useMemo(
        () => ({
            ...labels.labelsColors,
            [FIRST_NODE_LABEL]: {
                color: `repeating-linear-gradient(
                    -45deg,
                    #aaa,
                    #aaa 10px,
                    #bbb 10px,
                    #bbb 20px
                  )`,
                colorInactive: "#ddd8",
                border: "#888",
                var_name: "_special"
            }
        }),
        [labels]
    );
    const labelNames: { [id: string]: string } = useMemo(
        () => ({ ...labels.names, [FIRST_NODE_LABEL]: "[All Items]" }),
        [labels]
    );

    const visibleNodes = funnel.filter((node) => node.idx === 0 || trackedLabels.has(node.label!));
    const minHeight = Math.min(...visibleNodes.filter((node) => node.count > 0).map((node) => node.height));
    const canvasHeight = Math.max(MIN_NODE_HEIGHT / minHeight, MIN_CANVAS_HEIGHT);

    const headers = visibleNodes.map((node) => (
        <div key={node.label} className={styles.header}>
            {labelNames[node.label]}
        </div>
    ));

    const showWeights = numberColumn !== undefined;

    //
    // tooltips
    //
    const [tooltip, setTooltip] = useState<ReactNode>(null);

    const handleMouseEnterNode =
        (node: FunnelNode, idx: number) =>
        (event: React.MouseEvent): void => {
            let tooltipClass;
            if (frameRef && frameRef.current) {
                const frameRect = frameRef.current.getBoundingClientRect();
                if (event.clientX > frameRect.right - 200) {
                    tooltipClass = styles.tooltipLeft;
                }
            }

            const nextNode = idx < visibleNodes.length ? visibleNodes[idx + 1] : undefined;
            type Out = { label: string | undefined; weight: number; count: number };
            let out1: Out | undefined;
            let out2: Out | undefined;
            if (nextNode) {
                out1 = { label: nextNode.label, weight: nextNode.weight, count: nextNode.count };
                out2 = { label: undefined, weight: node.weight - nextNode.weight, count: node.count - nextNode.count };
            }

            const io = (out: Out): ReactNode => {
                const style = out.label ? labelColors[out.label] : ({} as LabelStyle);
                return (
                    <Fragment>
                        <div className={styles.tooltipRow}>
                            <div
                                className={cn(styles.tooltipStatus, { [styles.tooltipDropOffStatus]: !style.color })}
                                style={{ backgroundColor: style.color }}
                            >
                                {out.label ? labelNames[out.label] : "↓"}
                            </div>
                            {showWeights && (
                                <div className={cn(styles.tooltipNumber, styles.textRight)}>
                                    {formatWeight(out.weight, unitTag)}
                                    <div className={styles.tooltipSubNumber}>
                                        {formatFloat(node.weight !== 0 ? (100 * out.weight) / node.weight : 100)}%
                                    </div>
                                </div>
                            )}
                            <div className={cn(styles.tooltipNumber, styles.textRight)}>
                                {out.count}
                                <div className={styles.tooltipSubNumber}>
                                    {formatFloat((100 * out.count) / node.count)}%
                                </div>
                            </div>
                        </div>
                    </Fragment>
                );
            };

            setTooltip(
                <TrackingTooltip className={tooltipClass}>
                    <div className={styles.tooltipContent}>
                        {showWeights && (
                            <div className={styles.tooltipRow}>
                                <div className={styles.tooltipTitle}>Weight</div>
                                <div className={styles.tooltipNumber}>
                                    {formatWeight(node.weight, unitTag)}
                                    <div className={styles.tooltipSubNumber}>
                                        {formatFloat(
                                            visibleNodes[0].weight !== 0
                                                ? (100 * node.weight) / visibleNodes[0].weight
                                                : 100
                                        )}
                                        % of total
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={styles.tooltipRow}>
                            <div className={styles.tooltipTitle}>Items</div>
                            <div className={styles.tooltipNumber}>
                                {node.count}
                                <div className={styles.tooltipSubNumber}>
                                    {formatFloat((100 * node.count) / visibleNodes[0].count)}% of total
                                </div>
                            </div>
                        </div>
                        {out1 && out2 && (
                            <Fragment>
                                <div className={styles.separator} />
                                <div className={styles.tooltipRow}>
                                    <div className={cn(styles.tooltipTitle, styles.strong)}>Out ⇨</div>
                                    {showWeights && (
                                        <div className={cn(styles.tooltipTitle, styles.textCenter)}>Weight</div>
                                    )}
                                    <div className={cn(styles.tooltipTitle, styles.textCenter)}>Items</div>
                                </div>
                                {io(out1)}
                                {io(out2)}
                            </Fragment>
                        )}
                    </div>
                </TrackingTooltip>
            );
        };

    const handleMouseLeave = (): void => {
        setTooltip(null);
    };

    //
    // nodes
    //

    const nodes = visibleNodes.map((node, idx) => {
        const style = labelColors[node.label];
        const selected = node === selectedNode;
        const top = showDeadEnds ? 0 : canvasHeight * node.top;
        if (node.count === 0) {
            return (
                <div key={node.label} className={styles.nodeColumn}>
                    <div
                        className={styles.emptyNode}
                        style={{
                            backgroundColor: style.color,
                            borderColor: style.border
                        }}
                    >
                        No items
                    </div>
                </div>
            );
        }
        return (
            <div key={node.label} className={styles.nodeColumn}>
                <div
                    className={cn(styles.node, {
                        [styles.selectedNode]: selected,
                        [styles.unselectedNode]: !selected && selectedNode !== undefined
                    })}
                    style={{
                        top,
                        height: canvasHeight * node.height,
                        background: style.color,
                        borderColor: style.border
                    }}
                    onClick={(event): void => {
                        event.stopPropagation();
                        onSelectNode(node);
                    }}
                    onMouseEnter={handleMouseEnterNode(node, idx)}
                    onMouseLeave={handleMouseLeave}
                >
                    {showWeights && (
                        <div className={styles.count}>
                            <span className={"unicodeIcon"}>⚖</span> {formatWeight(node.weight, unitTag)}
                        </div>
                    )}
                    <div className={showWeights ? styles.percent : styles.count}>{node.count}</div>
                </div>
            </div>
        );
    });

    // generate all the gradients
    const defsCache: ReactNode[] = [];
    for (let i = 0; i < visibleNodes.length - 1; i++) {
        const styleFrom = labelColors[visibleNodes[i].label];
        const styleTo = labelColors[visibleNodes[i + 1].label];
        const fillKey = `lfill_${styleFrom.var_name}_${styleTo.var_name}`;
        const fillKey2 = `lfill_${styleFrom.var_name}_deadend`;

        defsCache.push(
            <linearGradient key={fillKey} id={fillKey}>
                <stop offset="5%" stopColor={styleFrom.colorInactive} />
                <stop offset="95%" stopColor={styleTo.colorInactive} />
            </linearGradient>
        );
        if (showDeadEnds) {
            defsCache.push(
                <linearGradient key={fillKey2} id={fillKey2} gradientTransform="rotate(90)">
                    <stop offset="60%" stopColor={styleFrom.colorInactive} />
                    <stop offset="100%" stopColor={getTransparentRGB(styleFrom.colorInactive)} />
                </linearGradient>
            );
        }
    }

    let lowestDeadEndY = canvasHeight;

    const connectors = visibleNodes.map((node, idx) => {
        if (idx > visibleNodes.length - 2) {
            return null;
        }
        const fromX = idx * NODE_WIDTH + NODE_WIDTH * GOLDEN_RATIO;
        const nextNode = visibleNodes[idx + 1];
        let fromY: number;
        let toY: number;
        let heightL: number;

        let deadEndFromY = 0;
        let deadEndToY = 0;

        const DEAD_END_WIDTH = NODE_WIDTH * GOLDEN_RATIO_REST * GOLDEN_RATIO;
        const DEAD_END_EXTRA = 20;

        if (showDeadEnds) {
            fromY = 0;
            toY = 0;
            heightL = Math.round(nextNode.height * canvasHeight);

            deadEndFromY = Math.round(nextNode.height * canvasHeight);
            deadEndToY = Math.round(node.height * canvasHeight) + DEAD_END_EXTRA;
            deadEndToY = Math.max(deadEndToY, deadEndFromY + DEAD_END_WIDTH + DEAD_END_EXTRA);
            lowestDeadEndY = Math.max(lowestDeadEndY, deadEndToY);
        } else {
            fromY = Math.round(node.top * canvasHeight);
            toY = Math.round(nextNode.top * canvasHeight);
            heightL = Math.round(node.height * canvasHeight);
        }

        const showConnector = nextNode.count > 0;
        const showDeadEnd = showDeadEnds && node.count - nextNode.count > 0;

        return (
            <Fragment key={node.label}>
                {showConnector && (
                    <SvgConnector
                        fromX={fromX}
                        fromY={fromY}
                        toX={fromX + NODE_WIDTH * GOLDEN_RATIO_REST}
                        toY={toY}
                        heightL={heightL}
                        heightR={Math.round(nextNode.height * canvasHeight)}
                        paddingY={0}
                        lineX={3}
                        styleFrom={labelColors[node.label]}
                        styleTo={labelColors[nextNode.label]}
                    />
                )}
                {showDeadEnd && (
                    <SvgDeadEnd
                        fromX={fromX}
                        fromY={deadEndFromY}
                        toX={fromX + DEAD_END_WIDTH}
                        toY={deadEndToY}
                        fillKey={`lfill_${labelColors[node.label].var_name}_deadend`}
                        text={`${node.count - nextNode.count}`}
                    />
                )}
            </Fragment>
        );
    });

    //
    // scaling
    //

    const [scale, setScale] = useState<number>(1.0);
    const frameRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [frameHeight, setFrameHeight] = useState<number>();

    const handleZoomIn = (): void => {
        setScale(Math.min(scale + 0.1, MAX_SCALE));
    };

    const handleZoomOut = (): void => {
        setScale(Math.max(scale - 0.1, MIN_SCALE));
    };

    useEffect(() => {
        // give time for containerRef to update itself and return correct bounding rect
        setTimeout(() => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setFrameHeight(Math.max(rect.height, MIN_CANVAS_HEIGHT) + 10); // +10 to account for scrollbar height
            }
        }, 1);
    }, [scale, canvasHeight]);

    const handleViewAll = (): void => {
        if (frameRef.current) {
            const frameRect = frameRef.current.getBoundingClientRect();
            const sX = frameRect.width / (visibleNodes.length * NODE_WIDTH);
            const sY = DEFAULT_CANVAS_HEIGHT / canvasHeight;
            const s = Math.max(MIN_SCALE, Math.min(1.0, sX, sY));
            setScale(s);
        }
    };

    const handleResetScale = (): void => {
        setScale(1.0);
    };

    // prevent flicker. useEffect would be executed after render showing wrong scale, before correction
    useLayoutEffect(() => {
        handleViewAll();
    }, [funnel]); //eslint-disable-line

    //
    // drag to scroll
    //

    const { handleMouseDown, isScrolling } = useDragToScroll({ elem: frameRef.current, scrollX: true, scrollY: false });

    return (
        <div className={styles.canvas}>
            <div
                ref={frameRef}
                className={styles.scrollableFrame}
                style={{ height: frameHeight }}
                onClick={(): void => {
                    onSelectNode(null);
                }}
            >
                <div
                    ref={containerRef}
                    className={cn(styles.container, { [styles.grabbing]: isScrolling })}
                    style={{ transform: `scale(${scale})` }}
                    onMouseDown={handleMouseDown}
                >
                    <div className={styles.content}>
                        <div className={styles.headers}>{headers}</div>

                        <div className={styles.funnel} style={{ height: lowestDeadEndY }}>
                            <div className={styles.grid}>
                                {visibleNodes.map((_, idx) => (
                                    <div key={idx} className={styles.gridColumn} />
                                ))}
                            </div>

                            <svg className={styles.svg} style={{ height: lowestDeadEndY }}>
                                <defs>{defsCache}</defs>
                                {connectors}
                            </svg>

                            <div className={styles.nodes}>{nodes}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.controls}>
                <ScaleControls
                    scale={scale}
                    minScale={MIN_SCALE}
                    maxScale={MAX_SCALE}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onViewAll={handleViewAll}
                    onReset={handleResetScale}
                />
            </div>

            {tooltip}
        </div>
    );
};

export default FunnelCanvas;
